// Neo's question after "Ask Neo" on a board panel (Mike, 2026-10-31, TOM-958;
// the iPad's TOM-954): "What would you like to change about the Wind panel?",
// a line the chat adds to the end of its own thread. The cloud never sees or
// stores it. While it is the newest message, the owner's next message names
// the panel as its request context, unseen. Anything after it (an answer, an
// alarm's message) and the panel goes with it.
//
// Pure, so cloud/web's Node vitest covers it (no DOM harness).

import type { NeoRequestContext } from './neoPanel';

/** What these helpers read of a chat message. */
export interface NeoPanelQuestionEntry {
  id: string;
  /** Set on Neo's question about a panel: the context the reply names. */
  panelQuestion?: NeoRequestContext;
}

/** The context the owner's next message names: the panel, while Neo's question about it is newest. */
export function neoPanelQuestionContext(
  messages: readonly NeoPanelQuestionEntry[]
): NeoRequestContext | null {
  return messages.at(-1)?.panelQuestion ?? null;
}

/**
 * The thread with Neo's `question` at its end. Such a question still
 * unanswered at the end gives way to it: Ask Neo on another panel.
 */
export function withNeoPanelQuestion<M extends NeoPanelQuestionEntry>(
  messages: readonly M[],
  question: M
): M[] {
  const kept = messages.at(-1)?.panelQuestion !== undefined ? messages.slice(0, -1) : messages;
  return [...kept, question];
}

/**
 * The thread read again from the server (`next`), with Neo's unanswered
 * question kept at its end when it ended the thread before (`prev`). The
 * server never had it: a read that replaces a thread of the chat's own lines,
 * or adds an alarm's message under it, would drop it or bury it.
 */
export function keepNeoPanelQuestionLast<M extends NeoPanelQuestionEntry>(
  prev: readonly M[],
  next: readonly M[]
): M[] {
  const question = prev.at(-1);
  if (question?.panelQuestion === undefined) return [...next];
  return [...next.filter((message) => message.id !== question.id), question];
}
